/**
 * src/services/telemetry/TelemetryProvider.ts
 *
 * Provider interface that all telemetry sources (browser geolocation,
 * MAVLink gateway, PX4 Web Serial, signed-telemetry services, etc.) must
 * implement.  Consumers attach once via `onUpdate`; adapters are
 * responsible for emitting a NORMALIZED record matching the backend's
 * telemetryNormalizer.js merge-shape so the two sides can round-trip.
 *
 * Intentionally tiny — adapters own their own state, and we never leak
 * transport-specific types into the app.
 */

export type TelemetrySourceType = 'browser' | 'mavlink' | 'px4' | 'composite' | 'signed_telemetry';

export interface NormalizedTelemetry {
  entityId: string | null;
  timestamp: number;
  clientTimestamp: number | null;
  sourceType: TelemetrySourceType;
  sourceFieldMap: Record<string, TelemetrySourceType>;
  trustLevel: 'unverified' | 'signed' | 'attested';
  signerPubKeyHash: string | null;
  signature: string | null;
  position: { lat: number; lon: number; alt_msl: number | null; alt_agl: number | null } | null;
  velocity: { vx: number | null; vy: number | null; vz: number | null; groundSpeed: number | null; airSpeed: number | null } | null;
  heading: { courseDeg: number | null; yawDeg: number | null; pitchDeg: number | null; rollDeg: number | null } | null;
  attitude: { rollDeg: number | null; pitchDeg: number | null; yawDeg: number | null } | null;
  battery: { voltage: number | null; current: number | null; remainingPct: number | null } | null;
  health: { linkQuality: number | null; rssi: number | null; satellites: number | null; fixType: number | null } | null;
  flightMode: string | null;
  armed: boolean | null;
  home: { lat: number; lon: number; alt_msl: number | null } | null;
  rawSourceSnapshot: any;
}

export interface TelemetryProvider {
  readonly sourceType: TelemetrySourceType;
  start(): Promise<void> | void;
  stop(): void;
  onUpdate(cb: (rec: NormalizedTelemetry) => void): () => void;
  onError(cb: (err: Error) => void): () => void;
  readonly isActive: boolean;
}

/**
 * Mixin helper: tiny listener-map used by every adapter.
 */
export class TelemetrySignal<T> {
  private listeners = new Set<(v: T) => void>();
  emit(v: T) { this.listeners.forEach((l) => { try { l(v); } catch { /* ignore listener errors */ } }); }
  on(cb: (v: T) => void): () => void {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }
}
